import { Fragment } from 'react';
import { Options as RichTextOptions } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import styled from 'styled-components';

import { useModal } from '../state/ModalProvider';

import * as Link from './Link';
import { Modal, UncontrolledProps as UncontrolledModalProps, ControlledProps as ControlledModalProps } from './Modal';
import RichText from './RichTextV2';
import CallToAction from './CallToAction';
import {
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  Heading5,
} from './Typography';

/**
 * A basic modal window without any extra functionality. Perfect for simple text popups, alerts, etc.
 * Just pass a ContentfulModal content type to the data prop and the CMS content will automatically render.
 * 
 * This component can be used in two ways: Uncontrolled (preferred) and Controlled.  
 * - Uncontrolled: The component manages its own state via ModalContext. Provide a "type" prop. The modal can be opened and closed via the "useModal" hook or query params.  
 * - Controlled: The parent component manages the modal's state. Provide "visible" and "onClose" prop. The modal can be opened and closed by updating the "visible" prop.  
 * 
 * Note: It is invalid to provide both "type" and "visible" props, as state must be managed in one place.  
*/

export interface UncontrolledProps extends UncontrolledModalProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any
}

export interface ControlledProps extends ControlledModalProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any
}

export type Props = UncontrolledProps | ControlledProps;

const StyledList = styled.ul`
  padding-left: 1em;
  text-align: left;
  display: inline-block;

  li {
    list-style-image: none;
  }
`;

const richTextOptions: RichTextOptions = {
  renderNode: {
    [BLOCKS.HEADING_1]: (_, children) => <Heading1 style={{ marginBottom: '0.5em' }}>{children}</Heading1>,
    [BLOCKS.HEADING_2]: (_, children) => <Heading2 style={{ marginBottom: '0.6em' }}>{children}</Heading2>,
    [BLOCKS.HEADING_3]: (_, children) => <Heading3 style={{ marginBottom: '0.7em' }}>{children}</Heading3>,
    [BLOCKS.HEADING_4]: (_, children) => <Heading4 style={{ marginBottom: '0.8em' }}>{children}</Heading4>,
    [BLOCKS.HEADING_5]: (_, children) => <Heading5 style={{ marginBottom: '0.9em' }}>{children}</Heading5>,
    [BLOCKS.UL_LIST]: (_, children) => <StyledList>{children}</StyledList>,
    [BLOCKS.OL_LIST]: (_, children) => <StyledList as="ol">{children}</StyledList>,
    [INLINES.HYPERLINK]: (node, children) => (
      <Link.Primary href={node.data.uri} fontSize="inherit">
        {children}
      </Link.Primary>
    ),
  },
};

const BasicModal = ({
  data,
  type,
  visible,
  onClose,
  ...props
}: Props) => {
  const { closeModal } = useModal();

  const handleButtonClicked = () => {
    if (type) closeModal(type);
    if (onClose) onClose();
  };

  const controlProps = type ? { type } : { visible };

  return (
    <Modal {...props} {...controlProps} onClose={onClose}>
      {data?.modalHeadingRTF && (
        <RichText document={data.modalHeadingRTF} options={richTextOptions} />
      )}
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      {data?.modalContentSections?.map((section: any) => {
        if (section?.internal?.type === 'ContentfulTextSection') {
          return (
            <Fragment key={section?.id}>
              {section?.headingRTF && (
                <RichText document={section.headingRTF} options={richTextOptions} />
              )}
              {section?.bodyRTF && (
                <RichText document={section.bodyRTF} options={richTextOptions} />
              )}
              {section?.optionalButton && (
                <CallToAction
                  href={section.optionalButton?.buttonUrl}
                  variant={section.optionalButton?.buttonType}
                  onClick={handleButtonClicked}
                >
                  {section.optionalButton?.buttonLabel}
                </CallToAction>
              )}
            </Fragment>
          );
        }
        return null;
      })}
      {data?.modalCTA?.buttonLabel && (
        <CallToAction
          href={data.modalCTA?.buttonUrl}
          variant={data.modalCTA?.buttonType}
          onClick={handleButtonClicked}
        >
          {data.modalCTA?.buttonLabel}
        </CallToAction>
      )}
    </Modal>
  );
};

export default BasicModal;
